import type React from "react";
import {Handle, type NodeProps, Position} from "@xyflow/react";
import {Link, Stack, Typography} from "@mui/material";
import {generatePath, Link as RouterLink, useParams} from "react-router";
import {routes} from "~/routing/routes";
import type {Supplier360RouteMatch} from "~/routing/catRoutes";
import {Tag} from "~/components/tag/Tag";
import {COLORS} from "~/style/colors-ts";
import {ChainTierArrow} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/ChainTierArrow";
import {IconWrapper} from "~/components/IconWrapper/IconWrapper";
import {supplyTypeStyle} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/supplyTypeStyles";
import {
    type CounterpartyNode,
    counterpartyName,
    humanizeCode,
} from "~/pages/supplier-360/tabs/SupplierValueChain/components/chain/valueChainLayout";

const HIDDEN_HANDLE: React.CSSProperties = {opacity: 0, pointerEvents: 'none'};

/**
 * One supplier or buyer — the design's `svc-node`: tier chip, name (linking to the counterparty's
 * own Supplier 360 profile when it's a resolved entity), the supply type
 * and kind of company. A card with its
 * own suppliers / buyers shows how many in a pill plus an arrow on its outer edge, and toggles
 * them on click, like the taxonomy builder.
 */
export const ChainCounterpartyNode: React.FC<NodeProps<CounterpartyNode>> = ({data}) => {
    const {link, direction, dimmed, childCount, collapsed} = data;
    const role = humanizeCode(link.supply_type);
    const supplyType = supplyTypeStyle(link.supply_type);
    const counterpartyType = humanizeCode(link.counterparty_type);
    const name = counterpartyName(link);
    // The tree only gives the counterparty's id, so its profile opens in the databag on screen.
    const {id: databagId} = useParams<Supplier360RouteMatch>();
    const profilePath = link.counterparty_entity !== null && databagId
        ? `${generatePath(routes.supplier_360, {id: databagId})}/${link.counterparty_entity}/overview`
        : null;
    // Upstream children are this company's own suppliers; downstream, its own buyers.
    const childNoun = direction === 'upstream'
        ? (childCount === 1 ? 'supplier' : 'suppliers')
        : (childCount === 1 ? 'buyer' : 'buyers');
    const childLabel = `${childCount} ${childNoun}`;

    return (
        <Stack
            justifyContent="center"
            gap={0.5}
            sx={{
                position: 'relative',
                height: '100%',
                px: 1.75,
                border: '1px solid',
                borderColor: 'grey.100',
                borderRadius: '12px',
                bgcolor: 'common.white',
                opacity: dimmed ? 0.35 : 1,
                transition: 'opacity 150ms, box-shadow 150ms',
                '&:hover': childCount > 0 ? {boxShadow: 2} : undefined,
            }}
        >
            <Handle type="target" position={Position.Left} isConnectable={false} style={HIDDEN_HANDLE}/>
            <Stack direction="row" alignItems="center" gap={1} minWidth={0}>
                {link.tier && (
                    <Tag
                        label={link.tier}
                        color="default"
                        variant="outlined"
                        size="small"
                        sx={{flexShrink: 0, minHeight: '14px', '&&': {borderRadius: 0.5}}}
                    />
                )}
                <Typography fontSize={13.5} fontWeight={600} noWrap title={name} flex={1} minWidth={0}>
                    {profilePath ? (
                        <Link
                            component={RouterLink}
                            to={profilePath}
                            color="inherit"
                            underline="hover"
                            // A link, not a pan or a tier toggle: keep the click off the canvas and the card.
                            className="nodrag nopan"
                            onClick={event => event.stopPropagation()}
                        >
                            {name}
                        </Link>
                    ) : name}
                </Typography>
            </Stack>
            {/* The icon lines up with the supply type, the first of the two lines beside it. */}
            <Stack direction="row" alignItems="flex-start" gap={1} minWidth={0}>
                <IconWrapper size="small" color={supplyType.color}>
                    <supplyType.Icon titleAccess={supplyType.label}/>
                </IconWrapper>
                <Stack minWidth={0} flex={1}>
                    <Typography variant="caption" color="text.secondary" noWrap title={role}>
                        {role}
                    </Typography>
                    <Typography variant="caption" color="grey.500" noWrap title={counterpartyType}>
                        {counterpartyType}
                    </Typography>
                </Stack>
                {childCount > 0 && (
                    <Typography
                        variant="caption"
                        fontWeight={600}
                        flexShrink={0}
                        title={`Click to ${collapsed ? 'show' : 'hide'} ${childLabel}`}
                        sx={{
                            px: 0.75,
                            border: '1px solid',
                            borderColor: collapsed ? 'grey.200' : COLORS.primary[400],
                            borderRadius: '10px',
                            color: collapsed ? 'text.secondary' : COLORS.primary[700],
                            bgcolor: collapsed ? 'common.white' : COLORS.primary[100],
                        }}
                    >
                        {childLabel}
                    </Typography>
                )}
            </Stack>
            {childCount > 0 && <ChainTierArrow direction={direction} collapsed={collapsed} childLabel={childLabel}/>}
            <Handle type="source" position={Position.Right} isConnectable={false} style={HIDDEN_HANDLE}/>
        </Stack>
    );
};
